<template>
  <li class="module-item" :class="{ 'module-item-completed': variant === 'completed', 'module-item-upcoming': variant === 'upcoming' }">
    <component
      :is="wrapperTag"
      class="module-link"
      v-bind="wrapperAttrs"
    >
      <span class="module-main">
        <i :class="iconClass" aria-hidden="true"></i>
        <span>
          <span class="module-name">
            <span v-if="programmeModule.number" class="module-number">{{ programmeModule.number }}</span>
            {{ programmeModule.name }}
          </span>
          <span class="module-subtext">{{ subtext }}</span>
        </span>
      </span>

      <span v-if="percentage" class="module-trailing" :aria-label="trailingLabel">{{ percentage }}</span>
    </component>
  </li>
</template>

<style scoped>
.module-item {
  list-style: none;
}

.module-link {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: center;
  text-decoration: none;
  color: var(--kate-type-primary);
  border: 1px solid var(--kate-panel-gradient-border);
  border-radius: 10px;
  padding: 10px 12px;
  background-color: var(--kate-panel-gradient);
}

.module-link:hover {
  background-color: var(--kate-background-alpha-light);
}

.module-item-upcoming .module-link {
  border: var(--kate-panel-light-border);
  cursor: default;
}

.module-item-upcoming .module-link:hover {
  background-color: var(--kate-panel-gradient);
}

.module-item-completed .module-link {
  border: 0;
  background-color: transparent;
}

.module-main {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.module-name {
  display: block;
  font-weight: 600;
  text-transform: capitalize;
}

.module-number {
  color: var(--kate-type-alt);
  font-weight: 500;
  margin-right: 4px;
}

.module-subtext {
  display: block;
  font-size: 0.85rem;
}

.module-trailing {
  font-weight: 600;
  white-space: nowrap;
}

.fa-circle-check {
  color: var(--kate-type-success);
}
</style>

<script>
import TimeMixin from '../../../mixins/time-mixins';

export default {
  mixins: [TimeMixin],

  props: {
    programmeModule: {
      type: Object,
      required: true,
    },
    variant: {
      type: String,
      default: 'in-progress',
      validator: value => ['in-progress', 'upcoming', 'completed'].includes(value),
    },
  },

  computed: {
    isNavigable() {
      return this.variant !== 'upcoming';
    },

    wrapperTag() {
      return this.isNavigable ? 'router-link' : 'span';
    },

    wrapperAttrs() {
      if (this.isNavigable) {
        return { to: this.modulePageTo };
      }
      return { 'aria-disabled': true };
    },

    modulePageTo() {
      return {
        name: 'module_landing',
        params: { moduleId: this.programmeModule.id },
      };
    },

    iconClass() {
      if (this.variant === 'completed') {
        return 'far fa-circle-check';
      }
      if (this.variant === 'upcoming') {
        return 'far fa-clock-three';
      }
      return 'fas fa-arrow-rotate-right';
    },

    subtext() {
      if (!this.programmeModule.expected_completion_date) {
        return 'No expected completion date';
      }
      if (this.variant === 'upcoming' && this.programmeModule.release_date) {
        const formattedRelease = this.parseTimestamp(this.programmeModule.release_date, undefined, 'MMM d, yyyy');
        return `Releases: ${formattedRelease}`;
      }
      const formattedDate = this.parseTimestamp(this.programmeModule.expected_completion_date, undefined, 'MMM d, yyyy');
      return `Due by: ${formattedDate}`;
    },

    percentage() {
      if (this.variant === 'upcoming') {
        return '';
      }
      if (this.variant === 'completed') {
        return `${Math.floor(this.programmeModule.percentage)}%`;
      }
      return `${Math.floor(this.programmeModule.percentage)}%`;
    },

    trailingLabel() {
      if (!this.percentage) {
        return '';
      }
      return `Completion percentage: ${this.percentage}`;
    },
  },
};
</script>
